// ══════════════════════════════════════════════════════════════════════════════
//  Campos de formulario: Input, Select (combobox propio), EditableCell, Checkbox, Segmented, Radio, RadioGroup y Toggle.
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { C, T, R, E, M, F, Z } from '../../design.js';
import { Icon } from './Icon.js';
import { Td } from './tabla.js';   // directo a la pieza: UI.js es el barril que importa este archivo
import { coincide, plegar } from '../../utils.js';
import { valorEnMayusculas, valorTransformado } from './teclear.js';

// Input — `error` (bool o string) tiñe borde/fondo con los tokens errorField*
//   y, si es string, muestra el mensaje bajo el campo. aria-invalid para AT.
//   `mayusculas`: el campo captura un NOMBRE DE IDENTIDAD (obra, cliente,
//   proyecto, destinatario) y lo entrega ya canónico, letra por letra, en vez
//   de dibujarlo en mayúsculas con CSS y guardar lo tecleado (ver nombres.logic.js).
//   `transformar`: la regla del campo (un código de 3 letras, un RFC) aplicada
//   al teclear. Las dos conservan el cursor donde se escribe (ui/teclear.js).
const Input = ({ label, value, onChange, type="text", placeholder="", required=false, error, mayusculas=false, transformar, style:sx={}, ...rest }) => (
  <div style={{display:"flex",flexDirection:"column",gap:4,...sx}}>
    {label && <label style={{...T.caption,color:C.textSub}}>{label}{required&&<span style={{color:C.danger}}> *</span>}</label>}
    <input type={type} value={value||""} onChange={e=>onChange(transformar?valorTransformado(e,transformar):mayusculas?valorEnMayusculas(e):e.target.value)} placeholder={placeholder}
      aria-invalid={error?true:undefined}
      className="gb-campo gb-i gb-focus"
      style={{padding:"8px 10px",borderRadius:R.ctl,border:`1px solid ${error?C.errorFieldBorder:C.border}`,fontSize:13,color:C.text,fontFamily:"inherit",background:error?C.errorField:C.surface}} {...rest} />
    {typeof error==="string" && error && <span style={{...T.micro,color:C.danger}}>{error}</span>}
  </div>
);

// ══════════════════════════════════════════════
//  SELECT — Custom combobox (popover propio, sin <select> nativo)
//  API drop-in: { label, value, onChange, options, required, style,
//                 placeholder, disabled, searchable, crear }
//  options: ["str", ...] | [{value, label}, ...]
//  - Búsqueda automática si options.length > 8 (override con `searchable`)
//  - Navegación por teclado: ↑↓ Enter Esc; abre con Enter/Space
//  - Posición: detecta espacio y abre arriba/abajo
//  - ARIA combobox + listbox
// ══════════════════════════════════════════════
// `soft`: variante RELLENA (gb-field-soft) para buscadores y filtros de panel —
// fondo surfaceSunken sin borde visible y radio R.lg; abierto vuelve a blanco.
// Es la única excepción documentada al control bordeado de radio R.ctl (DESIGN.md).
// `crear`: { etiqueta(q), alCrear(q), mayusculas } — el combobox también da de
// alta lo que no encuentra (2026-09-19, el dueño al aprobar un presupuesto de un
// cliente sin registro: "me obliga a elegir uno de los que ya existen... me
// tendría que salir y registrar al cliente"). Con texto en la búsqueda y ninguna
// opción que se llame exactamente así, el pie de la lista lo registra (clic o
// Enter). `mayusculas`: lo tecleado es un NOMBRE DE IDENTIDAD y se captura
// canónico (nombres.logic.js).
const Select = ({ label, value, onChange, options, required=false, style:sx={}, placeholder="— Seleccionar —", disabled=false, searchable, error, soft=false, crear }) => {
  const norm = useMemo(
    () => (options||[]).map(o => typeof o==="string" ? {value:o,label:o} : {value:o.value, label:o.label}),
    [options]
  );
  const useSearch = !!crear || (searchable !== false && (searchable === true || norm.length > 8));

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(-1);
  const [pos, setPos] = useState({ top:0, left:0, width:0, openUp:false, maxH:280 });

  const triggerRef = useRef(null);
  const popRef     = useRef(null);
  const searchRef  = useRef(null);
  const itemsRef   = useRef([]);
  const listboxId  = useId();   // id estable para cablear aria-controls -> listbox

  const filtered = useMemo(() => {
    if (!query.trim()) return norm;
    const q = query.trim().toLowerCase();
    return norm.filter(o => coincide(q, o.label));
  }, [norm, query]);

  const selected = useMemo(
    () => norm.find(o => String(o.value) === String(value ?? "")),
    [norm, value]
  );

  // La fila que da de alta va después de las opciones (índice filtered.length).
  const nuevo = crear ? query.trim() : "";
  const filaCrear = !!nuevo && !norm.some(o => plegar(String(o.label ?? "").trim()) === plegar(nuevo));
  const nFilas = filtered.length + (filaCrear ? 1 : 0);

  // useCallback: identidad estable salvo cambios en filtered.length/useSearch,
  // que eran justo las deps extra del efecto de posicionamiento (mismos triggers).
  const computePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const desired   = Math.min(280, Math.max(120, filtered.length * 32 + (useSearch ? 44 : 0) + (filaCrear ? 38 : 0) + 8));
    const openUp    = spaceBelow < desired && spaceAbove > spaceBelow;
    const maxH      = Math.min(desired, openUp ? spaceAbove : spaceBelow);
    // Clamp horizontal: alinear con el trigger; encoger el ancho solo si se
    // saldría del viewport (seguridad móvil, desktop sin cambio).
    const vw    = window.innerWidth;
    const left  = Math.max(margin, Math.min(r.left, vw - margin));
    const width = Math.min(r.width, vw - left - margin);
    setPos({
      top:   openUp ? r.top - maxH - 4 : r.bottom + 4,
      left,
      width,
      openUp, maxH,
    });
  }, [filtered.length, useSearch, filaCrear]);

  // Recompute position when opened or window resizes/scrolls
  useLayoutEffect(() => {
    if (!open) return;
    computePos();
    const onResize = () => computePos();
    const onScroll = () => computePos();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true); // capture: catches inner scrolls
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, computePos]);

  // Outside-click to close
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // On open: focus search if any, set highlight to selected
  useEffect(() => {
    if (!open) { setQuery(""); setHi(-1); return; }
    const initialIdx = filtered.findIndex(o => String(o.value) === String(value ?? ""));
    setHi(initialIdx >= 0 ? initialIdx : 0);
    if (useSearch) {
      // small delay to ensure popover mounted
      setTimeout(() => searchRef.current?.focus(), 0);
    }
    // Solo al abrir/cerrar: snapshot deliberado de filtered/value/useSearch para
    // fijar el resaltado inicial; re-ejecutar al teclear resetearia hi y el focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!open || hi < 0) return;
    const el = itemsRef.current[hi];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [hi, open]);

  const choose = (v) => { onChange(v); setOpen(false); triggerRef.current?.focus(); };
  const darDeAlta = () => { const q = nuevo; setOpen(false); triggerRef.current?.focus(); crear.alCrear(q); };

  const onKeyDown = (e) => {
    // When closed, Enter/Space/ArrowDown opens
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    // When open
    if (e.key === 'Escape')      { e.preventDefault(); setOpen(false); }
    else if (e.key === 'Tab')    { setOpen(false); /* allow default tab */ }
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (nFilas) setHi(i => (i + 1) % nFilas);
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (nFilas) setHi(i => (i - 1 + nFilas) % nFilas);
    }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (filaCrear && hi === filtered.length) { darDeAlta(); return; }
      const opt = filtered[hi];
      if (opt) choose(opt.value);
    }
    else if (e.key === 'Home') { e.preventDefault(); setHi(0); }
    else if (e.key === 'End')  { e.preventDefault(); setHi(nFilas - 1); }
  };

  // Trigger styles — same dimensions as old <select> for layout parity.
  // Abierto = enfocado: borde y anillo en C.focus (azul de marca), igual que el
  // foco de teclado de toda la app.
  const triggerStyle = {
    display:"flex", alignItems:"center", justifyContent:"space-between",
    padding: soft ? "7px 10px" : "8px 10px", borderRadius: soft ? R.lg : R.ctl,
    border:`1px solid ${open ? C.focus : (error ? C.errorFieldBorder : (soft ? "transparent" : C.border))}`,
    boxShadow: open ? `0 0 0 2px ${C.primaryLight}` : "none",
    fontSize:13, color: selected ? C.text : C.textMuted,
    fontFamily:"inherit",
    background: disabled ? C.bg : (error ? C.errorField : (soft && !open ? C.surfaceSunken : C.surface)),
    cursor: disabled ? "not-allowed" : "pointer",
    width:"100%", textAlign:"left",
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:4,...sx}}>
      {label && (
        <label style={{...T.caption,color:C.textSub}}>
          {label}{required && <span style={{color:C.danger}}> *</span>}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        className="gb-campo-select gb-press gb-i gb-focus r-valor"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-required={required || undefined}
        aria-disabled={disabled || undefined}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        onClick={() => { if (disabled) return; setOpen(o => !o); }}
        onKeyDown={onKeyDown}
        style={triggerStyle}
      >
        <span style={{overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",flex:1}}>
          {selected ? selected.label : placeholder}
        </span>
        <Icon name={open ? "chevron-up" : "chevron-down"} size={14} color={C.textMuted} style={{marginLeft:8,flexShrink:0}}/>
      </button>

      {open && createPortal(
        <div
          ref={popRef}
          id={listboxId}
          role="listbox"
          aria-label={typeof label === "string" ? label : undefined}
          onKeyDown={onKeyDown}
          style={{
            position:"fixed", top:pos.top, left:pos.left, width:pos.width,
            zIndex: Z.popover, background:C.flotante,
            border:`1px solid ${C.border}`, borderRadius:R.menu,
            boxShadow:E.menu,
            display:"flex", flexDirection:"column",
            maxHeight: pos.maxH, overflow:"hidden",
            animation: `${pos.openUp ? "gbPopUp" : "gbPopDown"} ${M.fast} ${M.easeOut}`,
          }}
        >
          {useSearch && (
            <div style={{padding:6, borderBottom:`1px solid ${C.borderLight}`, background:C.bg}}>
              <input
                ref={searchRef}
                value={query}
                onChange={e => { setQuery(crear?.mayusculas ? valorEnMayusculas(e) : e.target.value); setHi(0); }}
                onKeyDown={onKeyDown}
                placeholder={crear ? "Buscar o registrar…" : "Buscar…"}
                className="gb-i gb-focus"
                style={{
                  width:"100%", padding:"6px 8px", fontSize:12,
                  border:`1px solid ${C.border}`, borderRadius:R.ctl,
                  fontFamily:"inherit", background:C.surface,
                }}
              />
            </div>
          )}
          <div style={{overflowY:"auto", flex:1}}>
            {filtered.length === 0 ? (filaCrear ? null : (
              <div style={{padding:"14px 10px",fontSize:12,color:C.textMuted,textAlign:"center"}}>Sin resultados</div>
            )) : filtered.map((o, i) => {
              const isSel = String(o.value) === String(value ?? "");
              const isHi  = i === hi;
              return (
                <div
                  key={String(o.value)}
                  ref={el => { itemsRef.current[i] = el; }}
                  role="option"
                  aria-selected={isSel}
                  onMouseEnter={() => setHi(i)}
                  onMouseDown={(e) => { e.preventDefault(); choose(o.value); }}
                  style={{
                    padding:"7px 10px", fontSize:13, lineHeight:1.4,
                    background: isHi ? C.primaryLight : (isSel ? C.tableSelected : "transparent"),
                    color: isSel ? C.primary : C.text,
                    fontWeight: isSel ? 600 : 400,
                    cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    gap:8, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                  }}
                >
                  <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{o.label}</span>
                  {isSel && <Icon name="check-circle" size={12} color={C.primary}/>}
                </div>
              );
            })}
          </div>
          {filaCrear && (
            <div
              role="option"
              aria-selected={false}
              onMouseEnter={() => setHi(filtered.length)}
              onMouseDown={(e) => { e.preventDefault(); darDeAlta(); }}
              style={{
                padding:"9px 10px", fontSize:13, lineHeight:1.4, flexShrink:0,
                borderTop: filtered.length ? `1px solid ${C.borderLight}` : "none",
                background: hi === filtered.length ? C.primaryLight : "transparent",
                color:C.primary, cursor:"pointer",
                display:"flex", alignItems:"center", gap:8,
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              }}
            >
              <Icon name="plus" size={12} color={C.primary} style={{flexShrink:0}}/>
              <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{crear.etiqueta ? crear.etiqueta(nuevo) : `Registrar ${nuevo}`}</span>
            </div>
          )}
        </div>,
        document.body
      )}
      {typeof error==="string" && error && <span style={{...T.micro,color:C.danger}}>{error}</span>}
    </div>
  );
};


// EditableCell — <Td> con input para tablas densas. Marca de edición = border-left
//   2px accent (NO boxShadow inset). Estado error = wash errorField + texto danger +
//   aria-invalid. El foco lo da gb-focus (sin outline:'none' inline).
const EditableCell = ({ value, onChange, type = "text", align = "center", changed = false, error = false, disabled = false, width, placeholder = "", inputStyle = {}, style: sx = {}, ...rest }) => (
  <Td style={{ padding: 0, ...(width ? { width } : {}), ...(changed ? { borderLeft: `2px solid ${C.accent}` } : {}), ...sx }}>
    <input type={type} value={value ?? ""} disabled={disabled} placeholder={placeholder}
      onChange={e => onChange && onChange(e.target.value)}
      aria-invalid={error ? true : undefined}
      className="gb-i gb-focus"
      style={{
        width: "100%", boxSizing: "border-box", border: "none", borderRadius: 0,
        background: error ? C.errorField : "transparent",
        padding: "10px 8px", textAlign: align, fontSize: 12, ...F.num,
        color: error ? C.danger : C.text, fontFamily: "inherit", ...inputStyle,
      }} {...rest} />
  </Td>
);

// Checkbox — accesible (role=checkbox, teclado), soporta indeterminate.
const Checkbox = ({ checked = false, onChange, label, disabled = false, indeterminate = false, size = 18, style: sx = {} }) => {
  const on = checked || indeterminate;
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...sx }}>
      <span role="checkbox" aria-checked={indeterminate ? "mixed" : checked} tabIndex={disabled ? -1 : 0}
        className="gb-i gb-focus"
        onClick={() => { if (!disabled) onChange && onChange(!checked); }}
        onKeyDown={e => { if (!disabled && (e.key === " " || e.key === "Enter")) { e.preventDefault(); onChange && onChange(!checked); } }}
        style={{ width: size, height: size, borderRadius: R.xs, border: `1.5px solid ${on ? C.primary : C.borderMid}`, background: on ? C.primaryFill : C.surface, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {indeterminate
          ? <span style={{ width: size * 0.5, height: 2, background: C.onPrimary, borderRadius: 1 }} />
          : checked
            ? <svg width={size * 0.62} height={size * 0.62} viewBox="0 0 24 24" fill="none" stroke={C.onPrimary} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            : null}
      </span>
      {label && <span style={{ ...T.body, color: C.text }}>{label}</span>}
    </label>
  );
};

// Segmented — control segmentado: UNA caja con borde y N segmentos; el activo
//   lleva primaryLight + primary (el vocabulario del toggle de modo del
//   Despacho). Para elegir entre 2 y 4 opciones excluyentes de una sola línea;
//   con más opciones, o etiquetas largas, va un Select. Los valores pueden
//   ser booleanos: se comparan con ===. Sin overflow:hidden en la caja para
//   que el anillo de foco (box-shadow) del segmento no se recorte.
const Segmented = ({ value, onChange, options, size="md", disabled=false, style:sx={} }) => {
  const norm = (options||[]).map(o => typeof o==="string" ? { value:o, label:o } : o);
  const h = size==="sm" ? 28 : 32;
  const last = norm.length-1;
  return (
    <div role="radiogroup" style={{ display:"flex", width:"100%", boxSizing:"border-box", border:`1px solid ${C.border}`, borderRadius:R.ctl, background:C.surface, opacity:disabled?0.5:1, ...sx }}>
      {norm.map((o, i) => {
        const on = o.value === value;
        // Una opción puede venir apagada por sí sola (o.disabled): se ve fantasma y no se elige.
        const off = disabled || !!o.disabled;
        return (
          <button key={String(o.value)} type="button" role="radio" aria-checked={on} disabled={off} title={o.title}
            onClick={() => { if (!off && !on) onChange && onChange(o.value); }}
            className="gb-i gb-press gb-focus"
            style={{ flex:"1 1 0", minWidth:0, height:h, padding:"0 10px", border:"none", borderLeft:i?`1px solid ${C.border}`:"none",
              borderRadius: i===0 ? `${R.ctl-1}px 0 0 ${R.ctl-1}px` : i===last ? `0 ${R.ctl-1}px ${R.ctl-1}px 0` : 0,
              background:on?C.primaryLight:"transparent", color:on?C.primary:(o.disabled?C.textDisabled:C.textMed),
              ...(size==="sm"?T.micro:T.bodySm), fontWeight:on?600:500, fontFamily:"inherit",
              cursor:off?"not-allowed":"pointer", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

// Radio — accesible (role=radio, teclado). Para grupos usar RadioGroup.
const Radio = ({ checked = false, onChange, label, disabled = false, name, size = 18, style: sx = {} }) => (
  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...sx }}>
    <span role="radio" aria-checked={checked} tabIndex={disabled ? -1 : 0} data-name={name}
      className="gb-i gb-focus"
      onClick={() => { if (!disabled) onChange && onChange(true); }}
      onKeyDown={e => { if (!disabled && (e.key === " " || e.key === "Enter")) { e.preventDefault(); onChange && onChange(true); } }}
      style={{ width: size, height: size, borderRadius: R.round, border: `1.5px solid ${checked ? C.primary : C.borderMid}`, background: C.surface, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {checked && <span style={{ width: size * 0.5, height: size * 0.5, borderRadius: R.round, background: C.primaryFill }} />}
    </span>
    {label && <span style={{ ...T.body, color: C.text }}>{label}</span>}
  </label>
);

// RadioGroup — grupo real (role=radiogroup). options: ["str"] | [{value,label,disabled}].
//   El nombre del grupo llega por aria-labelledby (el id de su rótulo) o aria-label: sin
//   él, un lector de pantalla anuncia las opciones sin decir de qué son.
const RadioGroup = ({ value, onChange, options, name, style: sx = {}, "aria-labelledby": etiquetadoPor, "aria-label": etiqueta }) => (
  <div role="radiogroup" aria-labelledby={etiquetadoPor} aria-label={etiqueta} style={{ display: "flex", flexDirection: "column", gap: 8, ...sx }}>
    {(options || []).map(o => {
      const opt = typeof o === "string" ? { value: o, label: o } : o;
      return <Radio key={String(opt.value)} name={name} checked={String(value) === String(opt.value)}
        onChange={() => onChange && onChange(opt.value)} label={opt.label} disabled={opt.disabled} />;
    })}
  </div>
);

// Toggle — switch on/off (role=switch). El thumb desliza con M.fast.
const Toggle = ({ checked = false, onChange, label, disabled = false, style: sx = {} }) => (
  <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...sx }}>
    <span role="switch" aria-checked={checked} tabIndex={disabled ? -1 : 0}
      className="gb-interruptor gb-focus"
      onClick={() => { if (!disabled) onChange && onChange(!checked); }}
      onKeyDown={e => { if (!disabled && (e.key === " " || e.key === "Enter")) { e.preventDefault(); onChange && onChange(!checked); } }}
      style={{ width: 38, height: 22, borderRadius: R.pill, background: checked ? C.primaryFill : C.borderMid, position: "relative", flexShrink: 0, transition: `background-color ${M.fast} ${M.ease}` }}>
      <span style={{ position: "absolute", top: 2, left: checked ? 18 : 2, width: 18, height: 18, borderRadius: R.round, background: C.surface, boxShadow: E.e1, transition: `left ${M.fast} ${M.ease}` }} />
    </span>
    {label && <span style={{ ...T.body, color: C.text }}>{label}</span>}
  </label>
);


export { Checkbox, EditableCell, Input, Radio, RadioGroup, Segmented, Select, Toggle };
